import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    HostBinding,
    Input,
    OnChanges,
    SimpleChanges,
    TemplateRef,
    computed,
    inject,
    input,
    signal,
} from '@angular/core';

import { DisplayType, TotalsPrefix } from '@cds/betting-offer';
import { AutomationDirective } from '@frontend/sports/automation/core-feature';
import { EventModel, EventOption, EventOptionGroup, EventParticipantImage, SetTab } from '@frontend/sports/betting-offer/feature/model';
import {
    EpcotConfig,
    MarketGroupingConfig,
    MarketSliderConfiguration,
    Sitecore,
    SitecoreEventDetailsConfig,
} from '@frontend/sports/common/client-config-data-access';
import { FontResizerComponent } from '@frontend/sports/common/core/feature/font-resizer';
import { ShowInEpcotDirective } from '@frontend/sports/common/core/feature/show-in-epcot';
import { NumberDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { NativePriceFormatterPipe } from '@frontend/sports/odds/feature/native-price';
import { SourceOfPick } from '@frontend/sports/option/pick/feature/tracking';
import { OptionContextService, PriceboostCountSignpostComponent } from '@frontend/sports/price-boost/feature';
import { NOT_APPLICABLE, TrackingSource } from '@frontend/sports/tracking/feature';
import { MarketType } from '@frontend/sports/types/components/content';
import { ContentReadyTriggerDirective, NativeAppService, UrlService } from '@frontend/vanilla/core';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { toNumber } from 'lodash-es';

import { EventPickComponent } from '../option-pick/event-pick.component';
import { SHEET_VIEW_OPEN } from '../sheet-view-core/sheet-view.token';
import { Slider } from './component/market-slider/service/market-slider-utils.service';
import { OptionNameStrategy } from './option-name.strategy';
import { PlayerStatsComponent } from './player-stats/player-stats.component';

@Component({
    selector: 'ms-option',
    templateUrl: './option.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    hostDirectives: [ContentReadyTriggerDirective],
    imports: [
        ShowInEpcotDirective,
        NgTemplateOutlet,
        NgClass,
        PlayerStatsComponent,
        EventPickComponent,
        AutomationDirective,
        PriceboostCountSignpostComponent,
        IconCustomComponent,
        FontResizerComponent,
        NativePriceFormatterPipe,
    ],
    styleUrl: './option.component.scss',
})
export class OptionComponent implements OnChanges {
    @Input() event: EventModel;
    @Input() option: EventOption;
    @Input() tracking: TrackingSource;
    @Input() showPromoBadge = false;
    @Input() showOptionName = true;
    @Input() showSeperateOptionName?: boolean;
    @Input() betDetails?: string;
    @Input() showOptionAttribute = false;
    @Input() optionImages: NumberDictionary<EventParticipantImage> = {};
    @Input() imageTemplate: TemplateRef<any>;
    @Input() isShowOptionImage = false;
    @Input() showPlayerStats = false;
    @Input()
    set sourceOfPick(value: SourceOfPick | SourceOfPick[]) {
        if (Array.isArray(value)) {
            this._sourceOfPick = value;
        } else if (value !== undefined) {
            this._sourceOfPick = [value];
        }
    }
    readonly optionGroup = input.required<EventOptionGroup>();
    readonly sliderValue = input<Slider>();
    readonly firstParticipant = input<string>();
    readonly secondParticipant = input<string>();
    readonly optionIndex = input<number>(0);
    readonly expanded = input<boolean>(false);
    readonly customOptionNameTemplate = input<TemplateRef<any> | null>();

    optionNamePrefix = signal<string>('');
    optionNameBase = signal<string>('');

    private readonly NBSP = '\u00A0';
    private _sourceOfPick: SourceOfPick[] = [];

    private readonly optionNameStrategy = inject(OptionNameStrategy);
    private readonly urlService = inject(UrlService);
    private readonly sitecore = inject(Sitecore);
    private readonly sitecoreEventDetails = inject(SitecoreEventDetailsConfig);
    private readonly marketGroupingConfig = inject(MarketGroupingConfig);
    private readonly nativeAppService = inject(NativeAppService);
    private readonly epcotConfig = inject(EpcotConfig);
    private readonly marketSliderConfig = inject(MarketSliderConfiguration);
    private readonly optionContextService = inject(OptionContextService, { optional: true });
    private readonly isInSheetView = Boolean(inject(SHEET_VIEW_OPEN, { optional: true }));
    get sourceOfPick(): SourceOfPick {
        return this._sourceOfPick[0] ?? SourceOfPick.Other;
    }

    @HostBinding('class') className = 'option';

    optionBetDetails: NumberDictionary<string>;
    optionName: string;
    isEpcotEnabled = this.epcotConfig.isEnabled;

    readonly optionNameBaseColorClass = computed(() => {
        const slider = this.sliderValue();
        if (!slider) {
            return '';
        }

        return slider.currentValue !== slider.centerValue && !this.expanded() ? 'blue-highlight' : '';
    });

    private getBetDetails(option: EventOption, betDetails: string[] | undefined[]): string {
        if (this.sourceOfPick !== SourceOfPick.EventDetails && this.sourceOfPick !== SourceOfPick.TeamGrouping) {
            return this.betDetails || NOT_APPLICABLE;
        }

        if (this.optionGroup().detailedGrouping?.marketTabId && this.optionGroup().detailedGrouping?.optionColumnId) {
            betDetails[2] = option.optionColumnName;
        }

        return betDetails.join(';');
    }

    updateOptionBetDetails(): void {
        const betDetailsMap: NumberDictionary<string> = {};

        const betDetails: string[] | undefined[] = [];

        if (this.sourceOfPick === SourceOfPick.EventDetails || this.sourceOfPick === SourceOfPick.TeamGrouping) {
            if (this.optionGroup().detailedGrouping?.marketTabId && this.optionGroup().detailedGrouping?.optionColumnId) {
                betDetails[1] = this.optionGroup().periodName;
            } else {
                betDetails[1] = this.optionGroup().name;
            }
            const activeMarketTabId = toNumber(this.urlService.current().search.get('market'));
            switch (activeMarketTabId) {
                case SetTab.All:
                    betDetails[0] = this.sitecoreEventDetails.eventDetails.AllMarketsTemplate;
                    break;
                case SetTab.MyMarkets:
                    betDetails[0] = this.sitecoreEventDetails.eventDetails.MyMarkets;
                    break;
                case SetTab.MyBets:
                    betDetails[0] = this.sitecore.globalMessages.MyBetsTitle;
                    break;
                default: {
                    const name = this.event.optionSets.find((o) => o.id === activeMarketTabId)?.name ?? NOT_APPLICABLE;
                    betDetails[0] = this.event.optionSets.length > 1 ? name : NOT_APPLICABLE;
                    break;
                }
            }
        }
        for (const option of this.optionGroup().options) {
            let _betDetails = betDetails;
            betDetailsMap[option.id] = this.getBetDetails(option, _betDetails);
            _betDetails = [];
        }

        this.optionBetDetails = betDetailsMap;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.sourceOfPick || changes.optionGroup || changes.event) {
            this.updateOptionBetDetails();
        }

        if (changes.option) {
            this.optionName = this.getOptionName();
        }
        const _expanded = this.expanded();

        const isSpread = this.isSliderEnabledForType(DisplayType.Spread, this.marketSliderConfig.enableForSpread, _expanded);
        const isTotals = this.isSliderEnabledForType(MarketType.Totals, this.marketSliderConfig.enableForTotal, _expanded);

        if (isSpread && !this.isInSheetView && (changes.option || changes.optionIndex || this.firstParticipant() || this.secondParticipant())) {
            this.setOptionNameForSpread();
        } else if (isTotals && !this.isInSheetView) {
            this.setOptionNameForTotals();
        } else {
            this.setDefaultOptionName();
        }
    }

    getOptionName(): string {
        const optionName = this.optionNameStrategy.getOptionName(this.option);
        if (
            this.marketGroupingConfig.isOverUnderAbbreviationEnabled &&
            (this.option.marketAttribute || this.option.optionAttribute) &&
            this.option.totalsPrefix
        ) {
            const prefix =
                this.option.totalsPrefix === TotalsPrefix.Over
                    ? this.sitecore.eventGrid.SixPack_TotalsOver
                    : this.sitecore.eventGrid.SixPack_TotalsUnder;

            return `${prefix} ${this.option.marketAttribute || this.option.optionAttribute}`;
        }

        if (this.isPriceBoost && this.nativeAppService.isTerminal) {
            return `${this.option.optionMarketName} - ${optionName}`;
        }

        if (this.showOptionAttribute) {
            return this.option.optionAttribute!;
        }

        return optionName;
    }

    get isPriceBoost(): boolean {
        return !!this.optionContextService?.isPriceBoost && !!this.option.showBoostedPrice;
    }

    private setOptionNameForSpread(): void {
        const _firstParticipant = this.firstParticipant();
        const _secondParticipant = this.secondParticipant();

        const prefix = _firstParticipant && _secondParticipant ? (this.optionIndex() % 2 === 0 ? _firstParticipant : _secondParticipant) : '';

        this.optionNamePrefix.set(prefix);
        this.optionNameBase.set(this.optionName);
    }

    private setOptionNameForTotals(): void {
        const [prefix = '', ...rest] = this.optionName.split(' ');
        const base = rest.join(' ');
        const formattedPrefix = prefix + this.NBSP;

        this.optionNamePrefix.set(formattedPrefix);
        this.optionNameBase.set(base);
    }

    private setDefaultOptionName(): void {
        this.optionNamePrefix.set('');
        this.optionNameBase.set(this.optionName);
    }

    private isSliderEnabledForType(type: DisplayType | MarketType, isTypeEnabled: boolean, isExpanded: boolean): boolean {
        return (
            this.optionGroup().detailedGrouping?.name === type &&
            isTypeEnabled &&
            this.marketSliderConfig.activationThreshold <= this.optionGroup().options.length &&
            !isExpanded
        );
    }
}
